import { supabase } from '../supabase';

// Reportes para clientes que se comparten como link (Módulo de Reportes).
// El snapshot es la foto de los datos al generarlo; el link solo la lee.

export type SharedReportPeriod =
  | { kind: 'season' }
  | { kind: 'range'; from: string; to: string };

export interface SharedReport {
  id: number;
  created_at: string;
  exporter_id: number;
  created_by: number | null;
  kind: 'clients';
  title: string;
  season: string;
  species_id: number | null;
  receivers: string[];
  sections: string[];
  period: SharedReportPeriod;
  share_token: string;
  is_active: boolean;
  deactivated_at: string | null;
}

export interface SharedReportStats {
  report_id: number;
  views: number;
  visitors: number;
  last_viewed_at: string | null;
}

export interface NewSharedReport {
  exporter_id: number;
  created_by: number | null;
  title: string;
  season: string;
  species_id: number | null;
  receivers: string[];
  sections: string[];
  period: SharedReportPeriod;
  snapshot: unknown;
}

export interface PublicSharedReport<Snapshot = unknown> {
  title: string;
  created_at: string;
  snapshot: Snapshot;
}

const REPORT_COLUMNS =
  'id, created_at, exporter_id, created_by, kind, title, season, species_id, receivers, sections, period, share_token, is_active, deactivated_at';

export const getSharedReportUrl = (token: string) =>
  `${window.location.origin}/r/${token}`;

export const getSharedReports = async (
  exporterId: number
): Promise<SharedReport[]> => {
  const { data, error } = await supabase
    .from('shared_reports')
    .select(REPORT_COLUMNS)
    .eq('exporter_id', exporterId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as SharedReport[];
};

export const getSharedReportStats = async (
  reportIds: number[]
): Promise<SharedReportStats[]> => {
  if (reportIds.length === 0) return [];
  const { data, error } = await supabase
    .from('shared_report_view_stats')
    .select('report_id, views, visitors, last_viewed_at')
    .in('report_id', reportIds);

  if (error) throw error;
  return (data || []) as SharedReportStats[];
};

// Foto completa de un reporte, para descargarlo en PDF desde la admin.
export const getSharedReportSnapshot = async <Snapshot = unknown>(
  reportId: number
): Promise<Snapshot> => {
  const { data, error } = await supabase
    .from('shared_reports')
    .select('snapshot')
    .eq('id', reportId)
    .single();

  if (error) throw error;
  return data.snapshot as Snapshot;
};

export const createSharedReports = async (
  reports: NewSharedReport[]
): Promise<SharedReport[]> => {
  const { data, error } = await supabase
    .from('shared_reports')
    .insert(reports)
    .select(REPORT_COLUMNS);

  if (error) throw error;
  return (data || []) as SharedReport[];
};

export const setSharedReportActive = async (
  reportId: number,
  isActive: boolean
): Promise<void> => {
  const { error } = await supabase
    .from('shared_reports')
    .update({
      is_active: isActive,
      deactivated_at: isActive ? null : new Date().toISOString(),
    })
    .eq('id', reportId);

  if (error) throw error;
};

// Lee el reporte del link público y registra la vista. Devuelve null si el
// link no existe o está desactivado.
export const getPublicSharedReport = async <Snapshot = unknown>(
  token: string,
  visitorId: string | null
): Promise<PublicSharedReport<Snapshot> | null> => {
  const { data, error } = await supabase.rpc('get_shared_report', {
    p_token: token,
    p_visitor_id: visitorId,
  });

  if (error) throw error;
  return (data as PublicSharedReport<Snapshot> | null) ?? null;
};
